import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlay, faCalendar, faStar, faClock, faSpinner, faImage, faComments, faArrowLeft, faPlayCircle } from '@fortawesome/free-solid-svg-icons';
import { MoviesAPI, CommentsAPI, PopularityAPI } from '../../api';
import { AppContext } from '../../context/AppContext';
import { AuthContext } from '../../context/AuthContext';
import ContentSlider from '../Home/ContentSlider';
import WatchlistButton from '../Watchlist/WatchlistButton';
import { formatRuntime, formatRating } from '../../utils/formatters';
import { ContentTabs, MovieComments } from '../Comments';

// Optimized CSS
const DetailsContainer = styled.div`
  position: relative;
`;

const Backdrop = styled.div`
  height: 550px; // Increased height for better display
  background-image: ${props => `linear-gradient(to bottom, 
    rgba(0, 0, 0, 0.4) 0%, 
    rgba(0, 0, 0, 0.4) 40%, 
    rgba(0, 0, 0, 0.5) 60%,
    rgba(0, 0, 0, 0.6) 75%,
    rgba(0, 0, 0, 0.7) 85%,
    rgba(0, 0, 0, 0.8) 95%,
    rgba(12, 12, 12, 0.9) 100%), 
    url(${props.image})`};
  background-color: #111;
  background-size: cover;
  background-position: center 20%; // Better positioning
  position: relative;
  border-bottom: none;
  
  @media (max-width: 768px) {
    height: 350px;
    background-position: center 20%;
  }
`;

const FallbackBackdrop = styled.div`
  height: 450px;
  background: linear-gradient(to bottom, 
    rgba(20, 20, 20, 1) 0%, 
    rgba(22, 22, 22, 0.95) 40%, 
    rgba(18, 18, 18, 0.98) 70%,
    rgba(17, 17, 17, 0.99) 90%,
    var(--background-dark) 100%);
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  border-bottom: none;
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(to top, 
      var(--background-dark) 0%, 
      rgba(18, 18, 18, 0.98) 5%,
      rgba(18, 18, 18, 0.95) 10%,
      rgba(18, 18, 18, 0.8) 20%,
      rgba(18, 18, 18, 0.5) 40%,
      rgba(18, 18, 18, 0.2) 70%,
      transparent 85%);
    pointer-events: none;
  }
  
  svg {
    font-size: 60px;
    color: #333;
    z-index: 1;
  }
  
  @media (max-width: 768px) {
    height: 300px;
  }
`;


const BottomFade = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 250px; // Increased height for smoother gradient
  background: linear-gradient(to top, 
    var(--background-dark) 0%, 
    rgba(18, 18, 18, 0.98) 15%,
    rgba(18, 18, 18, 0.95) 30%,
    rgba(18, 18, 18, 0.85) 45%,
    rgba(18, 18, 18, 0.7) 60%,
    rgba(18, 18, 18, 0.5) 75%,
    rgba(18, 18, 18, 0.3) 85%,
    rgba(18, 18, 18, 0.1) 95%,
    transparent 100%);
  z-index: 1;
  pointer-events: none;
`;

const Content = styled.div`
  padding: 0 40px;
  margin-top: -220px;
  position: relative;
  z-index: 2;
  display: flex;
  flex-direction: row;
  
  @media (max-width: 1024px) {
    padding: 0 30px;
    margin-top: -200px;
  }
  
  @media (max-width: 768px) {
    flex-direction: column;
    padding: 0 20px;
    margin-top: -150px;
  }
`;

const TopContent = styled.div`
  display: flex;
  width: 100%;
  
  @media (min-width: 769px) {
    display: none;
  }
  
  @media (max-width: 768px) {
    flex-direction: row;
    align-items: flex-start;
    margin-bottom: 15px;
  }
`;

const Poster = styled.div`
  width: 220px;
  height: 330px;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 15px 35px rgba(0, 0, 0, 0.7);
  position: relative;
  transition: transform 0.3s ease;
  background-color: #1a1a1a;
  border: 1px solid rgba(255, 255, 255, 0.05);
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.8);
  }
  
  @media (max-width: 768px) {
    width: 120px;
    height: 180px;
    margin-bottom: 0;
    flex-shrink: 0;
  }
`;

const PosterDesktop = styled(Poster)`
  @media (max-width: 768px) {
    display: none;
  }
`;

const PosterMobile = styled(Poster)`
  @media (min-width: 769px) {
    display: none;
  }
`;

const PosterImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const FallbackPoster = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #1a1a1a 0%, #2a2a2a 100%);
  color: #333;
  
  svg {
    font-size: 40px;
  }
`;

const ImageErrorMessage = styled.div`
  position: absolute;
  bottom: 8px;
  left: 50%;
  transform: translateX(-50%);
  padding: 4px 8px;
  background-color: rgba(0, 0, 0, 0.6);
  border-radius: 4px;
  color: var(--text-dim);
  font-size: 12px;
  opacity: ${props => props.$visible ? 0.8 : 0};
  transition: opacity 0.3s ease;
  pointer-events: none;
  backdrop-filter: blur(4px);
`;

const Info = styled.div`
  flex: 1;
  padding: 10px 0 0 30px;
  
  @media (max-width: 768px) {
    padding: 10px 0 0 15px;
  }
`;

const InfoDesktop = styled(Info)`
  @media (max-width: 768px) {
    display: none;
  }
`;

const InfoMobile = styled(Info)`
  @media (min-width: 769px) {
    display: none;
  }
`;

const Title = styled.h1`
  font-size: 36px;
  font-weight: 700;
  margin-bottom: 15px;
  background: linear-gradient(to right, #fff, #aaa);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  
  @media (max-width: 768px) {
    font-size: 22px;
    margin-bottom: 10px;
  }
`;

const Meta = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  margin-bottom: 15px;
  
  span {
    margin-right: 15px;
    margin-bottom: 8px;
    color: var(--text-dim);
    display: flex;
    align-items: center;
    
    svg {
      margin-right: 5px;
      color: var(--primary-light);
    }
  }
  
  @media (max-width: 768px) {
    margin-bottom: 10px;
    
    span {
      margin-right: 10px;
      margin-bottom: 5px;
      font-size: 13px;
    }
  }
`;

const WatchedSpan = styled.span`
  background-color: rgba(59, 130, 246, 0.1);
  border-radius: 4px;
  padding: 2px 6px;
  font-size: 0.9em;
  display: inline-flex !important;
  align-items: center;
  
  svg {
    color: var(--primary-light) !important;
    margin-right: 4px;
    font-size: 0.9em;
  }
  
  @media (max-width: 768px) {
    padding: 1px 5px;
    font-size: 0.85em;
  }
`;

const Tags = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin-bottom: 20px;
  
  span {
    background-color: rgba(255, 255, 255, 0.1);
    backdrop-filter: blur(5px);
    padding: 5px 10px;
    border-radius: 5px;
    margin-right: 10px;
    margin-bottom: 10px;
    font-size: 14px;
    border: 1px solid rgba(255, 255, 255, 0.05);
    transition: all 0.3s ease;
    
    &:hover {
      background-color: rgba(255, 255, 255, 0.2);
      transform: translateY(-2px);
    }
  }
  
  @media (max-width: 768px) {
    margin-bottom: 15px;
    
    span {
      padding: 3px 8px;
      margin-right: 6px;
      margin-bottom: 6px;
      font-size: 12px;
    }
  }
`;

const Description = styled.p`
  margin-bottom: 30px;
  color: var(--text-dim);
  max-width: 800px;
  line-height: 1.7;
  font-size: 16px;
  
  @media (max-width: 768px) {
    margin-bottom: 20px;
    font-size: 14px;
    line-height: 1.5;
    width: 100%;
  }
`;

const DescriptionDesktop = styled(Description)`
  @media (max-width: 768px) {
    display: none;
  }
`;

const DescriptionMobile = styled(Description)`
  @media (min-width: 769px) {
    display: none;
  }
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
  margin-bottom: 30px;
  width: 100%;
  
  @media (max-width: 768px) {
    gap: 10px;
    margin-bottom: 20px;
    justify-content: space-between;
  }
`;

const ActionsDesktop = styled(Actions)`
  @media (max-width: 768px) {
    display: none;
  }
`;

const ActionsMobile = styled(Actions)`
  @media (min-width: 769px) {
    display: none;
  }
`;

const WatchButton = styled.button`
  background-color: var(--primary-color);
  color: white;
  border: none;
  padding: 10px 16px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(59, 130, 246, 0.4);
  flex: 1;
  min-width: 0;
  max-width: 180px;
  
  &:hover {
    background-color: var(--primary-light);
    transform: translateY(-3px);
    box-shadow: 0 7px 20px rgba(59, 130, 246, 0.5);
  }
  
  &:active {
    transform: translateY(-1px);
  }
  
  &:disabled {
    background-color: #444;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
  
  svg {
    margin-right: 8px;
    flex-shrink: 0;
  }
  
  @media (max-width: 768px) {
    padding: 8px 12px;
    font-size: 13px;
    flex: 1;
  }
`;

const ActionButtonWrapper = styled.div`
  flex: 1;
  max-width: 180px;
  min-width: 0;
  
  @media (max-width: 768px) {
    flex: 1;
  }
`;

const CommentButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(255, 255, 255, 0.1);
  color: var(--text-dim);
  border: none;
  padding: 10px 16px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  flex: 1;
  min-width: 0;
  max-width: 180px;
  
  &:hover {
    background-color: rgba(255, 255, 255, 0.2);
    color: var(--text-light);
    transform: translateY(-2px);
  }
  
  svg {
    margin-right: 6px;
    color: var(--text-dim);
    font-size: 13px;
    flex-shrink: 0;
  }
  
  &:hover svg {
    color: var(--primary-light);
  }
  
  @media (max-width: 768px) {
    padding: 8px 12px;
    font-size: 13px;
    flex: 1;
  }
`;

// Add these new styled components for the comments section
const CommentsSection = styled.div`
  padding: 0 40px;
  position: relative;
  
  @media (max-width: 768px) {
    padding: 0;
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: var(--background-dark);
    z-index: 100;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
  }
`;

const CommentBackButton = styled.button`
  display: flex;
  align-items: center;
  background-color: rgba(26, 26, 26, 0.8);
  color: var(--text-light);
  border: none;
  padding: 8px 15px;
  border-radius: 8px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-bottom: 15px;
  
  &:hover {
    background-color: rgba(59, 130, 246, 0.2);
    transform: translateY(-2px);
  }
  
  svg {
    margin-right: 8px;
    color: var(--text-dim);
  }
  
  @media (max-width: 768px) {
    position: fixed;
    top: 20px;
    left: 20px;
    z-index: 101;
    background-color: rgba(26, 26, 26, 0.9);
    backdrop-filter: blur(5px);
    border-radius: 50%;
    width: 48px;
    height: 48px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    box-shadow: 0 3px 15px rgba(0, 0, 0, 0.4);
    
    span {
      display: none;
    }
    
    svg {
      margin-right: 0;
      font-size: 22px;
      color: var(--text-light);
    }
  }
`;

const CommentsContent = styled.div`
  @media (max-width: 768px) {
    padding: 0 20px 20px;
    flex: 1;
    margin-top: 80px; /* Increase from 60px to 80px to match the new back button position */
  }
`;

// Add a CommentsHeader component after the CommentsContent component
const CommentsHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
  
  @media (max-width: 768px) {
    padding: 15px 20px 15px 75px;
    background-color: rgba(18, 18, 18, 0.95);
    position: sticky;
    top: 0;
    z-index: 5;
    backdrop-filter: blur(10px);
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  }
`;

const SimilarContent = styled.div`
  margin-top: 40px;
  padding-bottom: 50px;
`;

const LoadingOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  backdrop-filter: blur(5px);
`;

const LoadingSpinner = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  
  svg {
    font-size: 40px;
    color: var(--primary-light);
    animation: spin 1s linear infinite;
  }
  
  p {
    margin-top: 15px;
    color: white;
    font-size: 16px;
  }
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const MovieDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { refreshUserData } = useContext(AppContext);
  const { currentUser, openLoginModal } = useContext(AuthContext);
  const [movie, setMovie] = useState(null);
  const [similarMovies, setSimilarMovies] = useState([]);
  const [watchStatus, setWatchStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState(null);
  const [backdropError, setBackdropError] = useState(false);
  const [posterError, setPosterError] = useState(false);
  const [activeTab, setActiveTab] = useState('movie');
  const [commentCount, setCommentCount] = useState(0);
  const [loadComments, setLoadComments] = useState(false);
  
  // Add a state to track if we're on mobile
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  
  // Add effect to update isMobile state when window resizes
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);
  
  useEffect(() => {
    // Fetch movie details
    const fetchMovieDetails = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const response = await MoviesAPI.getById(id);
        
        // Check if response.data is the movie object directly or nested in a data property
        const movieData = response.data.data || response.data;
        const relatedContent = response.data.related || [];
        const watchStatusData = response.data.watchStatus || null;
        
        // Set movie data
        setMovie(movieData);
        setSimilarMovies(relatedContent);
        
        // Track this movie view using the new PopularityAPI
        try {
          await PopularityAPI.trackContentView('movie', id);
          console.log('Movie view tracked successfully');
        } catch (trackError) {
          console.error('Failed to track movie view:', trackError);
          // Don't set error state here - we don't want to break the page if tracking fails
        }
        
        // Log watchStatus data
        console.log('Watch Status from API:', watchStatusData);
        
        // Make sure watchStatus has valid numeric progress value
        if (watchStatusData && typeof watchStatusData.progress === 'number') {
          console.log('Setting watch status with numeric progress:', watchStatusData.progress);
          setWatchStatus(watchStatusData);
        } else if (watchStatusData) {
          // Fix any potential issues with watchStatus
          const fixedProgress = parseFloat(watchStatusData.progress) || 0;
          console.log('Fixing watch status progress:', watchStatusData.progress, '->', fixedProgress);
          setWatchStatus({
            ...watchStatusData,
            progress: fixedProgress
          });
        } else {
          console.log('No watchStatus data received from API');
        }
      } catch (err) {
        console.error('Error fetching movie details:', err);
        setError('Failed to load movie details. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchMovieDetails();
  }, [id]);
  
  // Add separate useEffect for comment count, with a slight delay
  useEffect(() => {
    // Only fetch comment count if movie data is loaded
    if (!movie) return;
    
    const fetchCommentCount = async () => {
      try {
        // Small delay to prioritize main content rendering
        setTimeout(async () => {
          const commentsResponse = await CommentsAPI.getMovieComments(id, {
            limit: 1,
            count_only: true
          });
          
          if (commentsResponse.data && commentsResponse.data.data) {
            setCommentCount(commentsResponse.data.data.total || 0);
          }
        }, 1000); // 1 second delay
      } catch (commentErr) {
        console.error('Error fetching comment count:', commentErr);
      }
    };
    
    fetchCommentCount();
  }, [movie, id]);
  
  // Clear up preloading in the useEffect hook to make it simpler
  useEffect(() => {
    if (!movie) return;
    
    // Test poster image loading
    if (movie.image) {
      const img = new Image();
      img.onload = () => setPosterError(false);
      img.onerror = () => setPosterError(true);
      img.src = movie.image;
    }
    
    // Test backdrop image loading
    const backdropUrl = movie.coverImage || movie.image;
    if (backdropUrl) {
      const img = new Image();
      img.onload = () => setBackdropError(false);
      img.onerror = () => setBackdropError(true);
      img.src = backdropUrl;
    }
  }, [movie]);
  
  const handlePosterError = () => {
    setPosterError(true);
  };

  const handleBackdropError = () => {
    setBackdropError(true);
  };
  
  const handleWatchClick = async () => {
    if (!movie || !movie.links || movie.links.length === 0) {
      alert('No video sources available for this movie.');
      return;
    }
    
    try {
      setActionLoading(true);
      
      // Update watch status only if user is logged in
      if (currentUser) {
        // Only update the watch status if it doesn't exist yet
        // This preserves the user's progress
        if (!watchStatus) {
          await MoviesAPI.updateWatchStatus(movie._id, {
            progress: 0,
            completed: false
          });
          refreshUserData();
        }
      }
      
      // Make sure we have valid links
      const validLinks = movie.links.filter(link => link && link.url && link.name);
      
      if (validLinks.length === 0) {
        throw new Error('No valid video sources found');
      }
      
      // Create navigation state
      const navigationState = { 
        id: movie._id,
        type: 'movie',
        title: movie.title,
        sources: validLinks.map(link => ({
          label: link.name,
          url: link.url
        })),
        description: movie.description,
        // Include the current watch progress info
        watchStatus: watchStatus || null
      };

      // Log navigation attempt
      console.log('Navigating to player with state:', navigationState);
      
      // Navigate to player
      navigate('/player', { 
        state: navigationState,
        replace: false // Ensure we don't replace the current history entry
      });
    } catch (err) {
      console.error('Error starting playback:', err);
      alert('Failed to start playback. Please try again.');
    } finally {
      setActionLoading(false);
    }
  };
  
  // Add a function to handle tab switching
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab === 'comments') {
      setLoadComments(true);
    }
  };
  
  // Add this function to handle back button action
  const handleBackFromComments = () => {
    handleTabChange('movie');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  
  if (loading) {
    return (
      <LoadingOverlay>
        <LoadingSpinner>
          <FontAwesomeIcon icon={faSpinner} spin />
          <p>Loading movie details...</p>
        </LoadingSpinner>
      </LoadingOverlay>
    );
  }
  
  if (error || !movie) {
    return <div className="error-message" style={{ padding: '50px', textAlign: 'center', color: 'var(--text-color)' }}>{error || 'Movie not found or failed to load.'}</div>;
  }
  
  // Only show comments in full screen if on mobile and comments tab is active
  if (isMobile && activeTab === 'comments') {
    return (
      <CommentsSection>
        <CommentBackButton onClick={handleBackFromComments}>
          <FontAwesomeIcon icon={faArrowLeft} /> 
          <span>Back to Movie</span>
        </CommentBackButton>
        <CommentsContent>
          {loadComments && <MovieComments movieId={id} />}
        </CommentsContent>
      </CommentsSection>
    );
  }
  
  // Ensure we have all the required data
  const duration = movie.durationMinutes ? formatRuntime(movie.durationMinutes) : formatRuntime(movie.duration);
  const rating = formatRating(movie.rating);
  const backdropImageUrl = movie.coverImage || movie.image;
  const movieTitle = movie.title || '';
  const movieDescription = movie.description || 'No description available.';
  const movieTags = movie.tags || [];
  const movieYear = movie.releaseYear || '';
  const movieLinks = movie.links || [];
  
  return (
    <DetailsContainer>
      {actionLoading && (
        <LoadingOverlay>
          <LoadingSpinner>
            <FontAwesomeIcon icon={faSpinner} spin />
            <p>Loading player...</p>
          </LoadingSpinner>
        </LoadingOverlay>
      )}
      
      {backdropImageUrl && !backdropError ? (
        <Backdrop image={backdropImageUrl}>
          <BottomFade />
        </Backdrop>
      ) : (
        <FallbackBackdrop>
          <FontAwesomeIcon icon={faImage} />
          <BottomFade />
        </FallbackBackdrop>
      )}
      
      <Content>
        {/* Desktop Layout */}
        <PosterDesktop>
          {movie.image && !posterError ? (
            <PosterImage
              src={movie.image}
              alt={`${movieTitle} Poster`}
              onError={handlePosterError}
            />
          ) : (
            <FallbackPoster>
              <FontAwesomeIcon icon={faImage} />
            </FallbackPoster>
          )}
          <ImageErrorMessage $visible={posterError && !!movie.image}>
            Image unavailable
          </ImageErrorMessage>
        </PosterDesktop>
        
        <InfoDesktop>
          <Title>{movieTitle}</Title>
          <Meta>
            {movieYear && (
              <span>
                <FontAwesomeIcon icon={faCalendar} />
                {movieYear}
              </span>
            )}
            {duration !== 'N/A' && (
              <span>
                <FontAwesomeIcon icon={faClock} />
                {duration}
              </span>
            )}
            {rating !== 'N/A' && (
              <span>
                <FontAwesomeIcon icon={faStar} />
                {rating}
              </span>
            )}
            {watchStatus && watchStatus.progress > 0 && (
              <WatchedSpan>
                <FontAwesomeIcon icon={faPlayCircle} />
                {watchStatus.progress}% watched
              </WatchedSpan>
            )}
          </Meta>
          <Tags>
            {movieTags.map((tag, index) => (
              <span key={index}>{tag}</span>
            ))}
          </Tags>
          <DescriptionDesktop>{movieDescription}</DescriptionDesktop>
          <ActionsDesktop>
            <WatchButton 
              onClick={handleWatchClick} 
              disabled={actionLoading || movieLinks.length === 0}
            >
              <FontAwesomeIcon icon={faPlay} /> 
              {watchStatus && watchStatus.progress > 0 ? 'Continue' : 'Watch Now'}
            </WatchButton>
            
            <ActionButtonWrapper>
              <WatchlistButton 
                contentType="movie" 
                contentId={movie._id} 
              />
            </ActionButtonWrapper>
            
            <CommentButton onClick={() => handleTabChange('comments')}>
              <FontAwesomeIcon icon={faComments} /> 
              Comments {commentCount > 0 && `(${commentCount})`}
            </CommentButton>
          </ActionsDesktop>
        </InfoDesktop>
        
        {/* Mobile Layout */}
        <TopContent>
          <PosterMobile>
            {movie.image && !posterError ? (
              <PosterImage
                src={movie.image}
                alt={`${movieTitle} Poster`}
                onError={handlePosterError}
              />
            ) : (
              <FallbackPoster>
                <FontAwesomeIcon icon={faImage} />
              </FallbackPoster>
            )}
            <ImageErrorMessage $visible={posterError && !!movie.image}>
              Image unavailable
            </ImageErrorMessage>
          </PosterMobile>
          
          <InfoMobile>
            <Title>{movieTitle}</Title>
            <Meta>
              {movieYear && (
                <span>
                  <FontAwesomeIcon icon={faCalendar} />
                  {movieYear}
                </span>
              )}
              {duration !== 'N/A' && (
                <span>
                  <FontAwesomeIcon icon={faClock} />
                  {duration}
                </span>
              )}
              {rating !== 'N/A' && (
                <span>
                  <FontAwesomeIcon icon={faStar} />
                  {rating}
                </span>
              )}
              {watchStatus && watchStatus.progress > 0 && (
                <WatchedSpan>
                  <FontAwesomeIcon icon={faPlayCircle} />
                  {watchStatus.progress}% watched
                </WatchedSpan>
              )}
            </Meta>
            <Tags>
              {movieTags.map((tag, index) => (
                <span key={index}>{tag}</span>
              ))}
            </Tags>
          </InfoMobile>
        </TopContent>
        
        <DescriptionMobile>{movieDescription}</DescriptionMobile>
        
        <ActionsMobile>
          <WatchButton 
            onClick={handleWatchClick} 
            disabled={actionLoading || movieLinks.length === 0}
          >
            <FontAwesomeIcon icon={faPlay} /> 
            {watchStatus && watchStatus.progress > 0 ? 'Continue' : 'Watch'}
          </WatchButton>
          
          <ActionButtonWrapper>
            <WatchlistButton 
              contentType="movie" 
              contentId={movie._id} 
            />
          </ActionButtonWrapper>
          
          <CommentButton onClick={() => handleTabChange('comments')}>
            <FontAwesomeIcon icon={faComments} /> 
            Comments
          </CommentButton>
        </ActionsMobile>
      </Content>
      
      {activeTab === 'comments' && !isMobile ? (
        <CommentsSection>
          <CommentBackButton onClick={handleBackFromComments}>
            <FontAwesomeIcon icon={faArrowLeft} /> 
            <span>Back to Movie</span>
          </CommentBackButton>
          <CommentsContent>
            {loadComments && <MovieComments movieId={id} />}
          </CommentsContent>
        </CommentsSection>
      ) : (
        <>
          {similarMovies && similarMovies.length > 0 ? (
            <SimilarContent>
              <ContentSlider title="You Might Also Like" items={similarMovies} type="movie" />
            </SimilarContent>
          ) : (
            <SimilarContent style={{padding: '0 40px 50px 40px', marginTop: '40px'}}>
              <h2 style={{ 
                fontSize: '24px', 
                fontWeight: '600', 
                marginBottom: '20px', 
                background: 'linear-gradient(to right, var(--primary-light), #fff)', 
                WebkitBackgroundClip: 'text', 
                MozBackgroundClip: 'text',
                backgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                MozTextFillColor: 'transparent',
                textFillColor: 'transparent'
              }}>
                You Might Also Like
              </h2>
              <p style={{ color: 'var(--text-dim)' }}>No similar movies found at the moment.</p>
            </SimilarContent>
          )}
        </>
      )}
    </DetailsContainer>
  );
};

export default MovieDetails;